import type { IOspAuthModule } from "@features/auth/compat/sdk-types";
import {
  appOspSecurity,
  DEFAULT_OSP_APP_ID,
  getEnvIndexBaseUrl,
  HEADERS,
  ospRegBrandHeaders,
} from "@api/osp";

import SwapApiClient from "agg-swap-client";

import { getDeviceType } from "utils";
import { AiClient } from "../../AiApi";
import { Api as QuestApi } from "../../QuestsApi";
import { ChatClient } from "../../ChatApi";
import { MemberClient } from "../../memberApi";
import { NotifyClient } from "../../NotifyApi";
import { OpenDataClient } from "../../openData";
import { OpenApiClient } from "../../openApi";
import { TrackerClient } from "../../TrackerApi";
import { AccountReadsClient } from "../../AccountReadsApi";
import { FinancialSyncStatusClient } from "../../FinancialSyncStatusApi";
import { config } from "@config/config";
import type { LoginState } from "@store/types";

const swapApiUrls = {
  pre: "https://finance-api.opensocial.co/swap",
  beta: "https://finance-dev.ospprotocol.xyz/swap",
  dev: "https://finance-dev.ospprotocol.xyz/swap",
  prod: "https://finance-api.edwealth.ai/swap",
} as const;

type EnvironmentKey = keyof typeof swapApiUrls;

/** 与 SDK AuthModule 构造一致：Index 根 URL / AppId 仅依赖 env 与 NEXT_PUBLIC_APP_ID */
const ospIndexBaseUrl = getEnvIndexBaseUrl(config.osp.environment);
const ospAppId = config.osp.appId;
const ospAppIdWithDefault = config.osp.appId || DEFAULT_OSP_APP_ID;
const ospBrandHeaders = ospRegBrandHeaders();

export const createSwapApiClient = () => {
  const env = process.env.NEXT_PUBLIC_ENV as EnvironmentKey;
  return new SwapApiClient(swapApiUrls[env] ?? swapApiUrls.prod);
};

export const createAiClient = (
  authModule: IOspAuthModule | null | undefined,
) => {
  if (!authModule) {
    return undefined;
  }
  return new AiClient({
    baseUrl: `${ospIndexBaseUrl}/ai`,
    securityWorker: appOspSecurity.getSecurityData,
    baseApiParams: {
      headers: {
        [HEADERS.APP_ID]: ospAppId,
        [HEADERS.CHAIN_ID]: config.chainConfig.id.toString(),
        ...ospBrandHeaders,
      },
    },
  });
};

export const createAccountReadsClient = (
  authModule: IOspAuthModule | null | undefined,
  fingerprint: string | null | undefined,
) => {
  if (!authModule || !fingerprint) {
    return undefined;
  }
  return new AccountReadsClient({
    baseUrl: ospIndexBaseUrl,
    securityWorker: appOspSecurity.getSecurityData,
    baseApiParams: {
      secure: true,
      headers: {
        "OS-Device-Id": fingerprint,
        "OS-Device-Type": (getDeviceType() ?? "unknown").toUpperCase(),
        [HEADERS.APP_ID]: ospAppId,
        [HEADERS.CHAIN_ID]: config.chainConfig.id.toString(),
        ...ospBrandHeaders,
      },
    },
  });
};

export const createFinancialSyncStatusClient = (
  authModule: IOspAuthModule | null | undefined,
  fingerprint: string | null | undefined,
) => {
  if (!authModule || !fingerprint) {
    return undefined;
  }
  return new FinancialSyncStatusClient({
    baseUrl: ospIndexBaseUrl,
    securityWorker: appOspSecurity.getSecurityData,
    baseApiParams: {
      secure: true,
      headers: {
        "OS-Device-Id": fingerprint,
        "OS-Device-Type": (getDeviceType() ?? "unknown").toUpperCase(),
        [HEADERS.APP_ID]: ospAppId,
        [HEADERS.CHAIN_ID]: config.chainConfig.id.toString(),
        ...ospBrandHeaders,
      },
    },
  });
};

export const createOpenApiClient = (
  authModule: IOspAuthModule | null | undefined,
) => {
  if (!authModule) {
    return undefined;
  }
  return new OpenApiClient({
    baseUrl: `${ospIndexBaseUrl}/platform`,
    securityWorker: appOspSecurity.getSecurityData,
    baseApiParams: {
      headers: {
        [HEADERS.APP_ID]: ospAppId,
        [HEADERS.CHAIN_ID]: config.chainConfig.id.toString(),
        ...ospBrandHeaders,
      },
    },
  });
};

export const createQuestApiClient = (
  authModule: IOspAuthModule | null | undefined,
  auth: LoginState["auth"] | undefined,
) => {
  if (!authModule || !auth) {
    return undefined;
  }
  return new QuestApi({
    baseUrl: `${config.api.baseUrl.quest}`,
    baseApiParams: {
      headers: {
        saas_id: config.api.saasId.quest,
        JWT_TOKEN: auth?.accessToken ?? "",
        idToken: auth?.idToken ?? "",
      },
    },
  });
};

export const createChatClient = (
  authModule: IOspAuthModule | null | undefined,
  fingerprint: string | undefined,
) => {
  if (!authModule || !fingerprint) {
    return undefined;
  }
  return new ChatClient({
    baseUrl: `${config.api.baseUrl.chat}`,
    baseApiParams: {
      secure: true,
      headers: {
        guestId: fingerprint,
        // perplexica CORS 未放行 OS-Reg-Brand；仅 ospprotocol Index 客户端需要该头
      },
    },
    securityWorker: appOspSecurity.getSecurityData,
  });
};

export const createNotifyClient = (
  authModule: IOspAuthModule | null | undefined,
  fingerprint: string | undefined,
) => {
  if (!authModule || !fingerprint) {
    return undefined;
  }
  return new NotifyClient({
    baseUrl: `${ospIndexBaseUrl}/notify`,
    baseApiParams: {
      secure: true,
      headers: {
        "OS-Device-Id": fingerprint,
        "OS-Device-Type": (getDeviceType() ?? "unknown").toUpperCase(),
        [HEADERS.APP_ID]: ospAppId,
        [HEADERS.CHAIN_ID]: config.chainConfig.id.toString(),
        ...ospBrandHeaders,
      },
    },
    securityWorker: appOspSecurity.getSecurityData,
  });
};

export const createMemberClient = (
  authModule: IOspAuthModule | null | undefined,
  fingerprint: string | undefined,
) => {
  if (!authModule || !fingerprint) {
    return undefined;
  }
  return new MemberClient({
    baseUrl: ospIndexBaseUrl,
    baseApiParams: {
      secure: true,
      headers: {
        "OS-Device-Id": fingerprint,
        [HEADERS.APP_ID]: ospAppId,
        [HEADERS.CHAIN_ID]: config.chainConfig.id.toString(),
        ...ospBrandHeaders,
      },
    },
    securityWorker: appOspSecurity.getSecurityData,
  });
};

export const createOpenDataClient = (
  authModule: IOspAuthModule | null | undefined,
  fingerprint: string | undefined,
) => {
  if (!authModule || !fingerprint) {
    return undefined;
  }
  return new OpenDataClient({
    baseUrl: `${ospIndexBaseUrl}/opendata`,
    baseApiParams: {
      secure: true,
      headers: {
        [HEADERS.APP_ID]: ospAppIdWithDefault,
        [HEADERS.CHAIN_ID]: config.chainConfig.id.toString(),
        ...ospBrandHeaders,
      },
    },
    securityWorker: appOspSecurity.getSecurityData,
  });
};

export const createTrackerClient = (
  authModule: IOspAuthModule | null | undefined,
  fingerprint: string | undefined,
) => {
  if (!authModule || !fingerprint) {
    return undefined;
  }
  return new TrackerClient({
    baseUrl: `${ospIndexBaseUrl}/tracker`,
    baseApiParams: {
      secure: true,
      headers: {
        "OS-Device-Id": fingerprint,
        "OS-Device-Type": (getDeviceType() ?? "unknown").toUpperCase(),
        [HEADERS.APP_ID]: ospAppId,
        [HEADERS.CHAIN_ID]: config.chainConfig.id.toString(),
        ...ospBrandHeaders,
      },
    },
    securityWorker: appOspSecurity.getSecurityData,
  });
};
